import { useState } from 'react'
import { useCallback } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import useRegions from '@/common/hooks/useRegions/useRegions'
import commonServices from '@/common/services/services'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { getShippingMethodsPayload } from './helpers'

const useShippingMethods = ({ storeId, setToastInfo }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    data: shippingMethodsData,
    loading: gettingShippingMethods,
    fetchData: getShippingMethodsData,
  } = useFetch(() => configurationServices.getShippingMethods({ storeId }), {
    doInitialRequest: true,
  })

  const { regions: regionsData, loading: fetchingRegions } = useRegions()

  const { data: carriersData, loading: fetchingCarriers } = useFetch(
    () => commonServices.getCarrierServices({ storeId, section: 'shipments' }),
    {
      doInitialRequest: true,
    }
  )

  const { fetchData: editShippingMethods, loading: editingShippingMethods } = useFetch(
    configurationServices.editShippingMethods,
    {
      doInitialRequest: false,
    }
  )

  const { fetchData: deleteShippingMethod } = useFetch(configurationServices.deleteShippingMethod, {
    doInitialRequest: false,
  })

  const [shippingMethods, setShippingMethods] = useState([])
  const [shippingMethodSelected, setShippingMethodSelected] = useState(null)
  const [showDeleteShippingMethodModal, setShowDeleteShippingMethodModal] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const { t } = useTranslation()

  const setInitValues = useCallback(() => {
    if (shippingMethodsData) {
      setShippingMethods(shippingMethodsData)
    }

    setHasChanges(false)
  }, [shippingMethodsData])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const revertChanges = () => {
    setInitValues()
  }

  const onSave = async () => {
    try {
      await editShippingMethods({
        storeId,
        shippingMethods: getShippingMethodsPayload(shippingMethods),
      })

      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })

      await getShippingMethodsData()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onDeleteShippingMethod = async () => {
    try {
      await deleteShippingMethod({ storeId, shippingMethodId: shippingMethodSelected.id })
      setToastInfo({ show: true, message: t('Método de envío eliminado'), type: 'success' })
      getShippingMethodsData()
      setShowDeleteShippingMethodModal(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleChangeEnabled = (id, checked) => {
    const shippingMethodsUpdated = shippingMethods.map(sm => {
      if (sm.id === id) {
        return { ...sm, is_enabled: checked }
      }
      return sm
    })

    setShippingMethods(shippingMethodsUpdated)
    setHasChanges(true)
  }

  const onEditShippingMethod = shippingMethod => {
    navigate(routes.configurationPage.logistics.shippingMethodDetailConfiguration.replace(':id', shippingMethod.id), {
      state: {
        childPageTitle: shippingMethod?.internal_name,
        selectedShippingMethod: shippingMethod,
        previousLocation: location,
      },
    })
  }

  const onDuplicateShippingMethod = shippingMethod => {
    navigate(
      routes.configurationPage.logistics.shippingMethodDetailConfiguration.replace(
        ':id',
        `duplicate-${shippingMethod.id}`
      ),
      {
        state: {
          selectedShippingMethod: { ...shippingMethod, internal_id: null },
          previousLocation: location,
        },
      }
    )
  }

  const onCreate = () => {
    navigate(routes.configurationPage.logistics.shippingMethodDetailConfiguration.replace(':id', 'new'), {
      state: { previousLocation: location },
    })
  }

  return {
    shippingMethods,
    setShippingMethods: param => {
      setShippingMethods(param)
      setHasChanges(true)
    },
    gettingShippingMethods,
    regions: regionsData,
    carriers: carriersData,
    fetchingCarriers,
    fetchingRegions,
    handleChangeEnabled,
    onSave,
    editingShippingMethods,
    shippingMethodSelected,
    setShippingMethodSelected,
    showDeleteShippingMethodModal,
    setShowDeleteShippingMethodModal,
    onDeleteShippingMethod,
    onEditShippingMethod,
    onDuplicateShippingMethod,
    onCreate,
    revertChanges,
    hasChanges,
  }
}

export default useShippingMethods
